import { useEffect, useRef, useState } from 'react';
import { Search, UserPlus, Loader2, Ticket, X } from 'lucide-react';
import { listMembers, enrollMemberClass, MemberRow } from '../../members/api';
import PersonAvatar from '../../../components/PersonAvatar';
import { createDebt, money, getCashboxCurrent } from '../../finances/api';
import ClaseSueltaModal from '../../finances/components/ClaseSueltaModal';
import { useToast } from '../../../context/ToastContext';

const MESES_L = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
  'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
/** '2026-08' → 'agosto' (sin new Date: evita el corrimiento de zona). */
const mesLargo = (m: string) => MESES_L[Number(m.slice(5, 7)) - 1] || m;

/**
 * Buscador «Añadir alumno a la clase» (Kydemy, video Karla 30-jul): teclear 2 letras,
 * clic y queda inscrito — sin pasar por la ficha de cada alumno. El backend es el mismo
 * de la ficha del alumno (POST /members/:id/classes); si estaba en la lista de espera
 * de esta clase, el backend lo promueve solo.
 */
const TIPO_PROSPECTO: Record<string, string> = {
  prospecto: 'Prospecto', visitante: 'Ya vino a clase suelta', ex_alumna: 'Ex-alumna',
};

/*
 * PROSPECTOS (F3, Karla 30-sep-2026): el buscador también los encuentra, pero NO los inscribe al primer
 * clic — inscribirlos los volvía alumnos. Ofrece «Cobrar clase suelta» (sale en Pasar lista de hoy sin
 * inscribirse) o, si ya se decidió, «Inscribir (pasa a alumno)».
 */
export default function AddStudentToClass({ classId, onAdded }: { classId: number; onAdded: () => void }) {
  const toast = useToast();
  const [q, setQ] = useState('');
  const [rows, setRows] = useState<MemberRow[]>([]);
  const [open, setOpen] = useState(false);
  const [buscando, setBuscando] = useState(false);
  const [agregando, setAgregando] = useState<number | null>(null);
  const [prospecto, setProspecto] = useState<MemberRow | null>(null);          // elegido, esperando qué hacer
  const [cobrar, setCobrar] = useState<{ id: number; name: string; caja: boolean } | null>(null);
  const boxRef = useRef<HTMLDivElement>(null);
  const seq = useRef(0);

  useEffect(() => {
    const s = q.trim();
    if (s.length < 2) { setRows([]); setOpen(false); setBuscando(false); return; }
    setBuscando(true);
    const mine = ++seq.current;
    const t = setTimeout(async () => {
      try {
        // Alumnas y prospectos en paralelo: la vista 'active' deja fuera a los prospectos a propósito.
        const [al, pr] = await Promise.all([
          listMembers({ search: s, status: 'active', page_size: 8 }),
          listMembers({ search: s, status: 'lead', page_size: 5 }).catch(() => ({ data: [] as MemberRow[] })),
        ]);
        if (mine !== seq.current) return;
        setRows([...al.data, ...pr.data.map((m) => ({ ...m, persona_tipo: m.persona_tipo || 'prospecto' }))]);
        setOpen(true);
      } catch { /* búsqueda fallida: se reintenta al teclear */ }
      finally { if (mine === seq.current) setBuscando(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  // Cerrar al hacer clic fuera (mousedown: antes de que el clic dispare otra cosa).
  useEffect(() => {
    const h = (e: MouseEvent) => { if (!boxRef.current?.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const esProspecto = (m: MemberRow) => !!m.persona_tipo && m.persona_tipo !== 'alumna';

  function elegir(m: MemberRow) {
    if (esProspecto(m)) { setProspecto(m); setOpen(false); return; }
    agregar(m);
  }

  async function abrirCobro(m: MemberRow) {
    let caja = false;
    try { caja = !!(await getCashboxCurrent()).open; } catch { /* sin dato: el modal avisa igual */ }
    setCobrar({ id: m.account_id, name: `${m.firstname} ${m.lastname || ''}`.trim(), caja });
    setProspecto(null); setQ(''); setRows([]);
  }

  async function agregar(m: MemberRow) {
    if (agregando) return;
    setAgregando(m.account_id);
    const nombre = `${m.firstname} ${m.lastname || ''}`.trim();
    try {
      const r = await enrollMemberClass(m.account_id, classId);
      if (r.already) toast.info(`${nombre} ya estaba en esta clase`);
      else toast.success(`${nombre} agregado a la clase`);
      setQ(''); setRows([]); setOpen(false);
      // Si le toca mensualidad y aún no la tiene, se ofrece crearla aquí mismo (Karla 30-jul).
      // Se PREGUNTA en vez de crearla sola: es dinero y la decisión es de ella.
      const sd = r.suggest_debt;
      if (sd && confirm(`${nombre} tiene plan «${sd.plan}» de ${money(sd.amount)}.\n\n¿Le genero su mensualidad de ${mesLargo(sd.month)}?`)) {
        try {
          // Sin `due_date`: lo resuelve el servidor con la regla del estudio (día base y, si está
          // encendida, la primera clase del alumno en el mes). Antes el navegador mandaba `-03`
          // fijo, así que cambiar la regla no habría movido esta fecha.
          await createDebt({ account_id: m.account_id, amount: sd.amount, month: sd.month,
            debt_type: 'membership',
            comment: `Mensualidad ${mesLargo(sd.month)}` });
          toast.success(`Mensualidad de ${mesLargo(sd.month)} generada`);
        } catch (e: any) { toast.error(e?.message || 'No se pudo generar la mensualidad'); }
      }
      onAdded();
    } catch (e: any) {
      toast.error(e?.message || 'No se pudo agregar');
    } finally { setAgregando(null); }
  }

  return (
    <div ref={boxRef} className="relative mb-4 max-w-md">
      <div className="relative">
        <Search size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-content-3" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => { if (rows.length) setOpen(true); }}
          placeholder="Añadir alumno a la clase…"
          className="w-full rounded-lg border border-line bg-surface py-2 pl-9 pr-9 text-sm text-content outline-none focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/30"
        />
        {buscando && <Loader2 size={15} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-content-3" />}
      </div>

      {open && (
        <div className="glass-menu absolute z-40 mt-1 w-full overflow-hidden rounded-xl border border-line shadow-xl">
          {rows.length === 0 ? (
            <p className="px-3 py-2.5 text-sm text-content-3">Sin resultados para «{q.trim()}»</p>
          ) : rows.map((m) => (
            <button key={m.account_id} type="button" onClick={() => elegir(m)} disabled={agregando !== null}
              className="flex w-full items-center gap-2.5 px-3 py-2 text-left transition hover:bg-surface-2 disabled:opacity-50">
              <PersonAvatar contactId={m.account_id} hasPhoto={m.has_photo}
                name={m.firstname} lastName={m.lastname} size="h-8 w-8" />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm text-content">{m.firstname} {m.lastname}</span>
                {esProspecto(m)
                  ? <span className="block truncate text-[11px] font-medium text-sky-600 dark:text-sky-400">{TIPO_PROSPECTO[m.persona_tipo || ''] || 'Prospecto'}</span>
                  : m.membership_name && <span className="block truncate text-[11px] text-content-3">{m.membership_name}</span>}
              </span>
              {agregando === m.account_id
                ? <Loader2 size={15} className="shrink-0 animate-spin text-content-3" />
                : <UserPlus size={15} className="shrink-0 text-content-3" />}
            </button>
          ))}
        </div>
      )}

      {prospecto && (
        <div className="mt-2 rounded-xl border border-sky-500/30 bg-sky-500/5 p-3 text-sm" data-testid="prospecto-opciones">
          <div className="mb-2 flex items-start justify-between gap-2">
            <span className="text-content">
              <b>{prospecto.firstname} {prospecto.lastname}</b> no es alumno ({(TIPO_PROSPECTO[prospecto.persona_tipo || ''] || 'prospecto').toLowerCase()}).
              Para pasarle lista hoy sin inscribirlo, cóbrale la clase suelta.
            </span>
            <button onClick={() => setProspecto(null)} aria-label="Cerrar" className="text-content-3 hover:text-content"><X size={15} /></button>
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => abrirCobro(prospecto)} data-testid="prospecto-cobrar"
              className="flex items-center gap-1.5 rounded-lg bg-accent-solid px-3 py-1.5 text-xs font-semibold text-on-accent transition hover:bg-accent-solid/90">
              <Ticket size={13} /> Cobrar clase suelta
            </button>
            <button data-testid="prospecto-inscribir"
              onClick={() => { const m = prospecto; if (confirm(`¿Inscribir a ${m.firstname} en esta clase?\n\nAl inscribirse pasa a ser ALUMNO (sale en Alumnos y se le generan mensualidades).`)) { setProspecto(null); agregar(m); } }}
              className="flex items-center gap-1.5 rounded-lg border border-line bg-surface px-3 py-1.5 text-xs text-content-2 transition hover:bg-surface-2">
              <UserPlus size={13} /> Inscribir (pasa a alumno)
            </button>
          </div>
        </div>
      )}

      {cobrar && (
        <ClaseSueltaModal cajaAbierta={cobrar.caja} personaInicial={{ id: cobrar.id, name: cobrar.name }} claseInicial={classId}
          onClose={() => setCobrar(null)} onDone={() => { setCobrar(null); onAdded(); }} />
      )}
    </div>
  );
}
